import Styles from './NewsletterPreview.module.scss'
import PostPreviewStyles from '../PostPreview.module.scss'
import { forwardRef } from 'react'
import cx from 'classnames'
import { ChevronDown2 } from 'blaze-ui/icons'
import { ArticlePostPreview } from '../ArticlePostPreview'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum, MailchimpProfile } from 'api/clients/ExternalIntegrationsClient'
import { useEmailIntegrations } from 'hooks/useEmailIntegrations'
import { ZapierLogo } from 'almanac-ui/icons/24'
import { EmailPostSettings } from 'api/clients/ScheduledPostsClient'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDocumentOverlay } from 'components/Previews/components/EditInDocumentOverlay/EditInDocumentOverlay'
import { AlmanacFileUnion } from '../Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const DefaultAvatar = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="41" height="40" viewBox="0 0 41 40" fill="none">
      <rect x="0.499756" width="40" height="40" rx="20" fill="black" fillOpacity="0.04" />
      <path
        d="M23.7998 28.9486C23.8299 28.9486 23.8628 28.9487 23.8984 28.9488C24.7738 28.9517 27.2824 28.9598 29.1833 27.5092C30.5608 26.4579 31.5812 24.8084 31.5812 22.9959C31.5812 21.727 31.3706 20.6837 30.6724 19.6868C30.6724 19.6868 31.844 18.6068 31.844 15.8013C31.844 13.7203 30.815 11.8519 28.1166 11.2422C27.2735 11.0516 24.9956 11.0509 24.9956 11.0509H18.4943L11.8913 18.3645L9.1554 28.9473L14.4342 28.9453L23.7998 28.9486Z"
        fill="black"
        fillOpacity="0.08"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.4342 28.9453L23.7998 28.9486C23.8299 28.9486 23.8628 28.9487 23.8984 28.9488C24.7738 28.9517 27.2824 28.9598 29.1833 27.5092C30.5608 26.4579 31.5812 24.8084 31.5812 22.9959C31.5812 21.727 31.3706 20.6837 30.6724 19.6868C30.6724 19.6868 31.844 18.6068 31.844 15.8013C31.844 13.7203 30.815 11.8519 28.1166 11.2422C27.2735 11.0516 24.9956 11.0509 24.9956 11.0509H18.4943L11.8913 18.3645L9.1554 28.9473L14.4342 28.9453Z"
        fill="black"
        fillOpacity="0.08"
      />
      <path
        d="M23.7998 28.9486C23.8299 28.9486 23.8628 28.9487 23.8984 28.9488C24.7738 28.9517 27.2824 28.9598 29.1833 27.5092C30.5608 26.4579 31.5812 24.8084 31.5812 22.9959C31.5812 21.727 31.3706 20.6837 30.6724 19.6868C30.6724 19.6868 31.844 18.6068 31.844 15.8013C31.844 13.7203 30.815 11.8519 28.1166 11.2422C27.2735 11.0516 24.9956 11.0509 24.9956 11.0509H18.4943L11.8913 18.3645L9.1554 28.9473L14.4342 28.9453L23.7998 28.9486Z"
        stroke="black"
        strokeOpacity="0.08"
        strokeWidth="2.27068"
      />
      <path
        d="M16.4381 27.0011L19.6496 12.8308H26.0323C26.5341 12.8308 27.6179 12.9111 28.3004 13.2322C29.6251 13.8544 30.0867 15.1189 30.0867 16.1827C30.0867 16.9053 29.886 17.6278 29.4444 18.2099C28.762 19.1332 27.8789 19.354 27.1764 19.5346C28.2803 20.0364 29.5849 20.6184 29.5849 22.7259C29.5849 23.8901 29.043 24.994 28.2 25.7567C26.9556 26.8807 25.2294 27.0011 24.3262 27.0011H16.4381ZM22.5198 15.3999L21.8173 18.4106H24.4466C25.1692 18.3705 26.6344 18.3103 26.6344 16.6845C26.6344 15.4802 25.6108 15.42 25.0689 15.3999H22.5198ZM21.2352 20.9797L20.4725 24.3918H23.5434C24.3061 24.3718 25.0287 24.3517 25.5506 23.6492C25.8316 23.2678 25.9119 22.8062 25.9119 22.4449C25.9119 22.2041 25.8717 21.9833 25.7914 21.7826C25.4301 21.0399 24.7477 21.0199 24.1857 20.9797H21.2352Z"
        fill="white"
      />
    </svg>
  )
}

interface NewsletterPreviewProps {
  almanacFile: AlmanacFileUnion
  isActive: boolean
  showHoverActionOverlay?: boolean
  onClose?: () => void
}

export const NewsletterPreview = forwardRef<HTMLDivElement, NewsletterPreviewProps>(
  ({ almanacFile, isActive, showHoverActionOverlay = false, onClose }, fRef) => {
    const { isMobile } = useResponsiveDesign()
    const externalIntegrations = useExternalIntegrations().data?.external_integrations
    const { connectedEmailIntegration } = useEmailIntegrations()
    const mailChimpIntegration = externalIntegrations?.find(
      (integration) => integration.provider === ExternalIntegrationEnum.Mailchimp,
    )
    const mailChimpProfile = mailChimpIntegration?.additional_data as MailchimpProfile

    const isZapierEmail = connectedEmailIntegration?.provider === ExternalIntegrationEnum.Zapier

    const resolveFullName = () => {
      if (mailChimpIntegration) return mailChimpProfile?.first_name + ' ' + mailChimpProfile?.last_name
      if (isZapierEmail) return connectedEmailIntegration?.name || 'Zapier'
      return 'Account not connected'
    }
    const fullName = resolveFullName()

    const renderAvatar = () => {
      if (mailChimpProfile?.avatar_url) return <img src={mailChimpProfile.avatar_url} alt="Mailchimp" />
      if (isZapierEmail) return <ZapierLogo size={40} />
      return <DefaultAvatar />
    }

    return (
      <div className={cx(Styles.newsletterPreviewContainer, { [Styles.isActive]: isActive })}>
        {isMobile ? null : (
          <div className={Styles.newsletterPreviewHeader}>
            <div className={Styles.titleContainer}>
              <div className={Styles.title}>
                {(almanacFile.scheduled_post?.post_settings as EmailPostSettings)?.subject_line}
              </div>
              <div className={Styles.inbox}>Inbox</div>
            </div>
            <div className={Styles.subjectContainer}>
              <div className={Styles.avatar}>{renderAvatar()}</div>
              <div className={Styles.emailSubject}>
                <div className={Styles.subjectLine}>
                  <div className={Styles.subject}>{fullName || 'Account not connected'}</div>
                  <div className={Styles.email}>{mailChimpProfile?.email || '<email@blaze.ai>'}</div>
                </div>
                <div className={Styles.toMe}>
                  to me
                  <ChevronDown2 size={20} />
                </div>
              </div>
            </div>
          </div>
        )}

        <ConditionalWrapper
          condition={showHoverActionOverlay}
          wrapper={(children) => (
            <EditInDocumentOverlay
              almanacFileKey={almanacFile.key}
              borderRadius={0}
              position="top-right"
              onClose={onClose}
            >
              {children}
            </EditInDocumentOverlay>
          )}
        >
          <div className={Styles.newsletterPreview}>
            <div
              className={cx(
                PostPreviewStyles.preview,
                PostPreviewStyles.isArticlePreview,
                PostPreviewStyles.isNewsletterPreview,
              )}
              ref={fRef}
              data-testid={`post-preview-${almanacFile?.key}`}
            >
              <ArticlePostPreview almanacFile={almanacFile} />
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    )
  },
)
